import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import logo from './logo.svg';

// --- CONFIG ---
// src/App.js
// Change this line:
const API_URL = ""; // <--- Uses relative path to hit the proxy
const PLATE_SIZE = 300; // Match the API generation size


function App() {
  const [view, setView] = useState('home'); // 'home', 'test', 'result'
  const [user, setUser] = useState({ name: '', hue: 0, duration: 15 });
  const [history, setHistory] = useState([]); // Local session history
  const [testData, setTestData] = useState(null); // Current API response
  const [finalResult, setFinalResult] = useState(null);

  // --- API HANDLERS ---
  const startTest = async () => {
    if (!user.name) return alert("Please enter your name.");
    
    try {
      const res = await fetch(`${API_URL}/api/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          user_name: user.name, 
          hue_angle: parseInt(user.hue),
          max_trials: parseInt(user.duration)
        }),
        credentials: 'include' // <--- ADD THIS! Critical for cookies.
      });

      if (!res.ok) throw new Error("Failed to start session"); // Safety check

      const data = await res.json();
      setTestData(data);
      setView('test');
    } catch (err) {
      console.error(err);
      alert("Failed to connect to backend.");
    }
  };

  const submitAnswer = async (isCorrect, waitPromise) => {
    try {
      const res = await fetch(`${API_URL}/api/next`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ correct: isCorrect }),
        credentials: 'include' 
      });

      if (res.status === 401) {
         alert("Session expired. Please refresh.");
         return;
      }

      const data = await res.json();

      // CRITICAL FIX: Wait for the minimum visual animation time BEFORE updating UI
      if (waitPromise) await waitPromise;
      
      if (data.test_finished) {
        setFinalResult(data.threshold_estimate);
        // Add to local history
        setHistory(prev => [...prev, {
          name: user.name,
          hue: user.hue,
          score: data.threshold_estimate.toFixed(2),
          date: new Date().toLocaleTimeString()
        }]);
        setView('result');
      } else {
        setTestData(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const abortTest = () => {
    setView('home');
    setTestData(null);
  };

  return (
    <div className="app-container">
      {view === 'home' && (
        <HomeScreen 
          user={user} 
          setUser={setUser} 
          onStart={startTest} 
          history={history} 
        />
      )}
      
      {view === 'test' && testData && (
        <TestScreen 
          data={testData} 
          hueAngle={user.hue}
          onNext={submitAnswer} 
          onAbort={abortTest}
        />
      )}
      
      {view === 'result' && (
        <ResultScreen 
          score={finalResult} 
          onBack={() => setView('home')} 
        />
      )}
    </div>
  );
}

// --- COMPONENT: HOME ---
function HomeScreen({ user, setUser, onStart, history }) {
  return (
    <>
      <img src={logo} alt="VisionGuard Logo" className="logo" /> 
      <h1>VisionGuard.net</h1>
      
      <div className="input-group">
        <label>Your Name</label>
        <input 
          type="text" 
          value={user.name} 
          onChange={(e) => setUser({...user, name: e.target.value})} 
          placeholder="e.g. John Doe"
        />
      </div>

      <div className="input-group">
        <label>Color Axis</label>
        <select 
          value={user.hue} 
          onChange={(e) => setUser({...user, hue: e.target.value})}
        >
          <option value="0">Red (Protan)</option>
          <option value="120">Green (Deutan)</option>
          <option value="240">Blue (Tritan)</option>
          <option value="90">Yellow (Macular)</option>
        </select>
      </div>

      <div className="input-group">
        <label>Test Duration</label>
        <select 
          value={user.duration} 
          onChange={(e) => setUser({...user, duration: e.target.value})}
        >
          <option value="5">Fast (5 Plates)</option>
          <option value="10">Medium (10 Plates)</option>
          <option value="15">Standard (15 Plates)</option>
        </select>
      </div>

      <button className="btn-primary" onClick={onStart}>Start Test</button>

      {history.length > 0 && (
        <div className="history-section">
          <h3>Previous Results</h3>
          {history.map((h, i) => (
            <div key={i} className="history-item">
              <span>{h.name} ({getHueName(h.hue)})</span>
              <strong>{h.score}</strong>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// --- COMPONENT: TEST SCREEN (THE CANVAS MAGIC) ---
function TestScreen({ data, hueAngle, onNext, onAbort }) {
  // We need 4 separate refs for the 4 plates
  // But actually, we will render 4 <IshiharaPlate> components
  
  const [animatingState, setAnimatingState] = useState(null); // { index: 0, type: 'correct' | 'wrong' }

  const handlePlateClick = (index) => {
    if (animatingState) return; 

    const isCorrect = (index === data.target_index);
    
    // Start Animation
    setAnimatingState({
      index: index,
      type: isCorrect ? 'correct' : 'wrong'
    });

    // Create a promise that resolves after 800ms
    const waitPromise = new Promise(resolve => setTimeout(resolve, 800));

    // Start fetching data immediately! 
    // The backend generation overlaps with the animation, hiding the latency perfectly.
    onNext(isCorrect, waitPromise).then(() => {
      setAnimatingState(null);
    });
  };

  const getColorName = (h) => {
    const map = { '0': 'Red', '120': 'Green', '240': 'Blue', '90': 'Yellow' };
    return map[h] || 'Color';
  };

  return (
    <>
      <div className="test-header">
        <div className="instruction">
          Choose the <strong>{getColorName(hueAngle)}</strong> circle.
        </div>
        <div className="sub-instruction">
          Guess if you don't know (Forced Choice)
        </div>
      </div>

      <div className="grid-container">
        {[0, 1, 2, 3].map(i => {
          
          // LOGIC: Determine animation for this specific plate
          let animType = null;
          if (animatingState) {
            if (animatingState.index === i) {
              // This is the clicked plate: do 'correct' or 'wrong'
              animType = animatingState.type;
            } else {
              // This is a non-clicked plate: just fade away
              animType = 'fade';
            }
          }

          return (
            <IshiharaPlate 
              key={i}
              index={i}
              dots={data[`circles_${i}`]}
              size={PLATE_SIZE}
              animation={animType} // Pass the calculated type
              onClick={() => handlePlateClick(i)}
            />
          );
        })}
      </div>

      <div className="progress-bar">
        Question {data.trial_count} / {data.max_trials || 15} 
        {/* Optional: Show threshold for debugging: | Est: {data.threshold_estimate.toFixed(1)} */}
      </div>
      
      <button className="btn-danger" onClick={onAbort} style={{ marginTop: '20px' }}>
        Abort Test
      </button>
    </>
  );
}

// --- SUB-COMPONENT: CANVAS PLATE ---
function IshiharaPlate({ index, dots, size, animation, onClick }) {
  const canvasRef = useRef(null);
  const SHOW_WHITE_BACKGROUND = false; 

  // --- 1. MOVED useEffect UP ---
  useEffect(() => {
    if (!dots || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let startTime;

    const render = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / 800, 1); 

      // 1. Clear
      ctx.clearRect(0, 0, size, size);

      // 2. White BG (Optional)
      if (SHOW_WHITE_BACKGROUND) {
        ctx.beginPath();
        ctx.arc(size/2, size/2, size/2, 0, 2*Math.PI);
        ctx.fillStyle = "white";
        ctx.fill();
      }

      // 3. Draw Dots
      dots.forEach(dot => {
        let [x, y, r, color] = dot;
        
        // --- ANIMATION LOGIC ---
        if (animation === 'correct') {
          // 1. CLICKED CORRECT: Green Explosion
          const center = size / 2;
          const dx = x - center;
          const dy = y - center;
          
          x = center + dx * (1 + progress * 3); 
          y = center + dy * (1 + progress * 3);
          
          r = r * (1 + progress * 1.5); 
          color = interpolateColor(color, '#00FF00', progress); 
          
          ctx.globalAlpha = Math.max(0, 1 - progress); 
        } 
        else if (animation === 'wrong') {
          // 2. CLICKED WRONG: Red Implosion (RESTORED!)
          r = r * (1 - progress * 0.8); // Shrink to 20%
          color = interpolateColor(color, '#FF0000', progress); // Turn Red
          // No Alpha fade here (or very slight), so we see the red dot shrink
        }
        else if (animation === 'fade') {
          // 3. NOT CLICKED: Just Fade Away
          ctx.globalAlpha = Math.max(0, 1 - progress * 1.5);
          // Optional: slight shrink
          r = r * 0.95; 
        }

        ctx.beginPath();
        ctx.arc(x, y, r, 0, 2 * Math.PI);
        ctx.fillStyle = color;
        ctx.fill();
        
        // Reset Alpha for next dot loop
        ctx.globalAlpha = 1.0; 
      });

      if (progress < 1 && animation) {
        animationFrameId = requestAnimationFrame(render);
      }
    };

    if (animation) {
      animationFrameId = requestAnimationFrame(render);
    } else {
      render(0); 
    }

    return () => cancelAnimationFrame(animationFrameId);
  }, [dots, size, animation, SHOW_WHITE_BACKGROUND]);

  // --- 2. EARLY RETURN ---
  if (!dots) {
      return <div style={{width: size, height: size, background: '#111', borderRadius: '50%'}} />;
  }

  return (
    <canvas 
      ref={canvasRef} 
      width={size} 
      height={size} 
      onClick={onClick}
      style={{ borderRadius: '50%', cursor: 'pointer' }} 
    />
  );
}

// --- COMPONENT: RESULT ---
function ResultScreen({ score, onBack }) {
  return (
    <>
      <h1>Test Complete</h1>
      <div style={{ fontSize: '4rem', fontWeight: 'bold', color: '#007bff' }}>
        {score.toFixed(1)}
      </div>
      <p style={{ opacity: 0.7 }}>Lower is better (0 = Perfect)</p>
      
      <button className="btn-primary" onClick={onBack}>
        Back to Start
      </button>
    </>
  );
}

// --- HELPER FUNCTIONS ---

function getHueName(h) {
  if (h == 0) return 'Red';
  if (h == 90) return 'Yellow';
  if (h == 120) return 'Green';
  if (h == 240) return 'Blue';
  return h;
}

// Simple linear interpolation between hex colors for animation
function interpolateColor(color1, color2, factor) {
  if (arguments.length < 3) return color1;
  
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color1);
  const c1 = result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [0,0,0];

  const result2 = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color2);
  const c2 = result2 ? [
    parseInt(result2[1], 16),
    parseInt(result2[2], 16),
    parseInt(result2[3], 16)
  ] : [0,0,0];

  const r = Math.round(c1[0] + factor * (c2[0] - c1[0]));
  const g = Math.round(c1[1] + factor * (c2[1] - c1[1]));
  const b = Math.round(c1[2] + factor * (c2[2] - c1[2]));

  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

export default App;